import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Users, Edit, UserPlus } from "lucide-react";
import type {
	OrgaAdminGruppeDTO,
	OrgaAdminUser,
} from "@/lib/orgaAdmin/orgaAdmin.types";
import { AssignmentPopup } from "./assignmentPopup.component";
import { useKorrekturStepAssignmentOperations } from "../contexts/korrekturStepAssignment.context";

interface GroupMembersTabProps {
	selectedBackendGroup?: OrgaAdminGruppeDTO;
	onNavigateToAssignment?: () => void;
}

/**
 * GroupMembersTab component for displaying the group members and adding a preassignment button based on user
 * @param selectedBackendGroup - The selected backend group
 * @param onNavigateToAssignment - The function to call when the assignment is made
 */
export function GroupMembersTab({
	selectedBackendGroup,
	onNavigateToAssignment,
}: GroupMembersTabProps) {
	const [selectedUser, setSelectedUser] = useState<OrgaAdminUser | null>(null);
	const [showAssignmentPopup, setShowAssignmentPopup] = useState(false);
	const { clearAssignmentFilters } = useKorrekturStepAssignmentOperations();

	const formatDate = (date: Date) => {
		return new Date(date).toLocaleDateString("de-DE", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const handleAssignKorrektor = (user: OrgaAdminUser) => {
		setSelectedUser(user);
		setShowAssignmentPopup(true);
	};

	const handlePopupClose = () => {
		setShowAssignmentPopup(false);
		setSelectedUser(null);
	};

	useEffect(() => {
		clearAssignmentFilters();
	}, [clearAssignmentFilters]);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Users className="h-5 w-5" />
						{selectedBackendGroup
							? `Benutzer*innen in ${selectedBackendGroup.title}`
							: "Gruppenmitglieder"}
					</div>
					<Button variant="outline" asChild>
						<a
							href={`/admin/user-group-management?groupId=${selectedBackendGroup?.id}`}
							className="flex items-center gap-2"
						>
							<Edit className="h-4 w-4" />
							Bearbeiten
						</a>
					</Button>
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Email</TableHead>
								<TableHead>Letzte Aktivität</TableHead>
								<TableHead className="w-32">Aktionen</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{selectedBackendGroup?.users ? (
								selectedBackendGroup.users.map((user: OrgaAdminUser) => (
									<TableRow key={user.id}>
										<TableCell className="font-medium">
											{user.name || "Unbekannt"}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{user.email}
										</TableCell>
										<TableCell>{formatDate(user.lastActive)}</TableCell>
										<TableCell>
											<Button
												variant="outline"
												size="sm"
												onClick={() => handleAssignKorrektor(user)}
												className="flex items-center gap-2"
											>
												<UserPlus className="h-4 w-4" />
												Zuweisen
											</Button>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={4}
										className="text-center text-muted-foreground py-8"
									>
										Keine Benutzer*innen in dieser Gruppe gefunden.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</CardContent>
			{showAssignmentPopup && selectedUser && selectedBackendGroup && (
				<AssignmentPopup
					user={selectedUser}
					klausuren={selectedBackendGroup.klausuren}
					onClose={handlePopupClose}
					onNavigateToAssignment={onNavigateToAssignment}
				/>
			)}
		</Card>
	);
}
